import React from 'react';
import { makeStyles, Theme, TableRow, TableCell, Grid } from '@bb-ui/react-library';

const useSkeletonStyles = makeStyles((theme: Theme) => ({
  skeleton: {
    backgroundColor: theme.palette.grey[300],
    borderRadius: theme.shape.borderRadius,
    animation: '$pulse 1.5s ease-in-out infinite',
    color: 'transparent',
    userSelect: 'none',
    display: 'inline-block',
  },
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.4 },
    '100%': { opacity: 1 },
  },
  text: {
    height: '1.2em',
    minWidth: '60px',
  },
  textSmall: {
    height: '1em',
    minWidth: '40px',
  },
  textLarge: {
    height: '1.4em',
    minWidth: '80px',
  },
  button: {
    height: '36px',
    width: '80px',
    borderRadius: theme.shape.borderRadius,
  },
  input: {
    height: '40px',
    borderRadius: theme.shape.borderRadius,
  },
  chip: {
    height: '24px',
    width: '80px',
    borderRadius: '12px',
  },
  narrow: { minWidth: '40px' },
  medium: { minWidth: '80px' },
  wide: { minWidth: '120px' },
  full: { width: '100%' },
}));

interface SkeletonProps {
  variant?: 'text' | 'textSmall' | 'textLarge' | 'button' | 'chip' | 'input';
  width?: 'narrow' | 'medium' | 'wide' | 'full' | string;
  height?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width = 'medium',
  height,
  className,
  style,
}) => {
  const classes = useSkeletonStyles();

  const skeletonClasses = [
    classes.skeleton,
    classes[variant],
    typeof width === 'string' && classes[width as keyof typeof classes],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const skeletonStyle = {
    ...style,
    ...(typeof width === 'string' && !classes[width as keyof typeof classes] && { width }),
    ...(height && { height }),
  };

  return <div className={skeletonClasses} style={skeletonStyle} />;
};

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  columnWidths?: Array<'narrow' | 'medium' | 'wide' | 'full'>;
  showCheckbox?: boolean;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 3,
  columns = 4,
  columnWidths = [],
  showCheckbox = false,
}) => {
  const renderRow = (index: number) => (
    <TableRow key={`skeleton-row-${index}`}>
      {showCheckbox && (
        <TableCell style={{ width: 50, textAlign: 'center' }}>
          <Skeleton variant="textSmall" width="narrow" />
        </TableCell>
      )}
      {Array.from({ length: columns }).map((_, colIndex) => (
        <TableCell key={`skeleton-cell-${colIndex}`}>
          <Skeleton variant="text" width={columnWidths[colIndex] || 'medium'} />
        </TableCell>
      ))}
    </TableRow>
  );

  return <>{Array.from({ length: rows }).map((_, index) => renderRow(index))}</>;
};

interface CardSkeletonProps {
  items?: Array<{
    label?: boolean;
    value?: 'narrow' | 'medium' | 'wide' | 'full';
    type?: 'text' | 'button' | 'chip' | 'input';
    gridSize?: 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  }>;
}

export const CardSkeleton: React.FC<CardSkeletonProps> = ({
  items = [
    { label: true, value: 'wide', gridSize: 6 },
    { label: true, value: 'medium', gridSize: 6 },
    { label: true, value: 'medium', type: 'chip', gridSize: 6 },
    { label: true, value: 'full', type: 'input', gridSize: 6 },
  ],
}) => (
  <>
    {items.map((item, index) => (
      <Grid size={item.gridSize ?? 6} key={index} style={{ marginBottom: '16px' }}>
        {item.label && (
          <div style={{ marginBottom: '8px' }}>
            <Skeleton variant="textSmall" width="medium" />
          </div>
        )}
        <Skeleton variant={item.type || 'text'} width={item.value || 'medium'} />
      </Grid>
    ))}
  </>
);

export const useSkeletonState = (isLoading: boolean, hasData = true) => ({
  showSkeleton: isLoading && !hasData,
  showLoadingOverlay: isLoading && hasData,
  isReady: !isLoading && hasData,
});
